import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'jspdf-autotable';
import PropTypes from 'prop-types';
import { Grid, Button, Menu, MenuItem, LinearProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CSVLink } from 'react-csv';
import ApiHelper from '../../utils/apiHelper';
import { API_URL } from '../../utils/constants';

const AssetExports = ({ assetInfo, downloadFileOption, selected, headers, selectedViewStatements, allData, disable = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { groupId, deviceTypeId, pageSize, total } = assetInfo;
    const [taskId, setTaskId] = useState(null);
    const [taskURL, setTaskURL] = useState(null);
    const [taskType, setTaskType] = useState(null);
    const open = Boolean(anchorEl);
    const [progress, setProgressValue] = useState(0);
    const [count, setCount] = useState(0);

    const downloadReport = useCallback(async () => {
        ApiHelper.get(`${API_URL.REPORT_GET}/${taskURL}/${taskType}`, {}, {}, {}, true, 'blob')
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${taskURL}.${taskType}`);
                link.click();
                setTaskURL(null);
            })
            .catch((err) => {
                console.error('Error downloading the file', err);
                setTaskURL(null);
            });
    }, [taskURL, taskType]);
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    useEffect(() => {
        if (taskURL) {
            downloadReport().then(() => {
                handleClose();
            });
        }
    }, [taskURL, downloadReport, handleClose]);

    useEffect(() => {
        const fetchTaskStatus = async () => {
            try {
                const response = await ApiHelper.get(`${API_URL.REPORT_TASK_STATUS}/${taskId}/`);
                const { status, data } = response.data;

                if (status === 'completed') {
                    setTaskType(data.type);
                    setTaskURL(data.file_url);
                    setTaskId(null);
                    setCount(0);
                } else {
                    setCount((prevCount) => {
                        const newCount = prevCount + 1;
                        if (total > 850) {
                            const newProgress = Math.floor(((850 * newCount) / total) * 100);
                            if (newProgress < 100) {
                                setProgressValue(newProgress);
                            }
                        }
                        return newCount;
                    });

                    if (count < 30) {
                        setTimeout(fetchTaskStatus, 50);
                    } else {
                        setProgressValue(0);
                        setTaskId(null);
                        setCount(0);
                    }
                }
            } catch (error) {
                console.error('Error fetching task status:', error);
                setProgressValue(0);
                setTaskId(null);
                setCount(0);
            }
        };

        if (taskId && taskURL === null) {
            console.log("Let's start task");
            fetchTaskStatus().then(() => {
                console.log('fetchTask triggered');
            });
        } else {
            handleClose();
        }
    }, [taskId, taskURL, count, total, handleClose]);

    const generateTask = async (type) => {
        if (taskId) {
            toast.warn('A task is already running...');
            return;
        }
        let taskPromise = null;
        if (type === 'pdf') {
            // post selected
            taskPromise = ApiHelper.post(`${API_URL.REPORT_GENERATE_TASK}/asset_inventory/`, {
                group_id: groupId === 0 ? [] : [groupId],
                device_type_id: deviceTypeId === 0 ? [] : [deviceTypeId],
                type,
                selected
            });
        } else {
            taskPromise = ApiHelper.post(`${API_URL.REPORT_GENERATE_TASK}/asset_inventory/`, {
                group_id: groupId === 0 ? [] : [groupId],
                // device_type_id: deviceTypeId === 0 ? [] : [deviceTypeId],
                headers: headers,
                statement: selectedViewStatements.length > 0 ? selectedViewStatements : [],
                type
            });
        }
        taskPromise.then((res) => {
            const { status, task_id } = res.data;
            if (status === 'in_progress') {
                setCount(0);
                setProgressValue(0);
                setTaskId(task_id);
            }
        });
    };

    const handleClickCSVReport = () => {
        generateTask('csv').then((r) => {});
    };

    const getCsvData = () => {
        // First row is headers
        const csvData = [headers.map((header) => header.label)];

        // If there are selected rows, use those, otherwise use selectedViewStatements
        const dataToExport = selected.length > 0 ? selected : allData;

        // Add data rows
        dataToExport.forEach((row) => {
            const csvRow = headers.map((header) => {
                const value = row[header.key];
                return value !== undefined && value !== null ? value : '';
            });
            csvData.push(csvRow);
        });

        return csvData;
    };
    const handleClickPDFReport = () => {
        generateTask('pdf').then((r) => {});
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    return (
        <div style={{ display: 'flex', margin: '5px', justifyContent: 'flex-end' }}>
            {taskId && (
                <div style={{ flexGrow: 1, marginRight: '10px', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                    <LinearProgress variant="determinate" value={progress}></LinearProgress>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3px' }}>
                        <p id="progress-text">{progress}%</p>
                    </div>
                </div>
            )}

            <Button
                disabled={disable}
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                variant="contained"
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon />}
            >
                Export
            </Button>
            {downloadFileOption === 'all' && (
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button'
                    }}
                >
                    <MenuItem>
                        <CSVLink
                            data={getCsvData()}
                            filename="asset_inventory.csv"
                            className="MuiMenuItem-root"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            onClick={handleClose}
                        >
                            Export CSV
                        </CSVLink>
                    </MenuItem>
                </Menu>
            )}
        </div>
    );
};

AssetExports.propTypes = {
    downloadFileName: PropTypes.string,
    downloadFileOption: PropTypes.string,
    assetInfo: PropTypes.object,
    selected: PropTypes.array,
    headers: PropTypes.array,
    selectedViewStatements: PropTypes.array,
    allData: PropTypes.array,
    disable: PropTypes.bool
};

export default AssetExports;
